import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, Col, Form, Row, Alert, Spinner, InputGroup } from "react-bootstrap";
import bg2 from "../assets/img/image.jpg";
import logo from "../assets/img/logo.jpeg";
import { API_BASE_URL } from "../constants/api";

export default function Signin2() {
  const navigate = useNavigate();
  
  // États pour le formulaire principal
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // États pour la 2FA
  const [step, setStep] = useState('login'); // 'login', 'verify'
  const [userId, setUserId] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/login.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
   
      if (!response.ok) {
        setError(data.message || "Email ou mot de passe incorrect");
        setLoading(false);
        return;
      }

      if (data.success) {
        if (data.requires_2fa) {
          setUserId(data.user_id);
          setStep('verify');
          setLoading(false);
        } else {
          localStorage.setItem("token", data.token);
          localStorage.setItem("user", JSON.stringify(data.user));
          navigate("/dashboard/finance");
        }
      } else {
        setError(data.message || "Email ou mot de passe incorrect");
      }
    } catch (err) {
      console.error("Erreur de connexion:", err);
      setError(`Erreur de connexion au serveur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      setError("Veuillez saisir le code à 6 chiffres");
      return;
    }

    setVerifying(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/verify_2fa.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ 
          user_id: userId, 
          code: twoFactorCode,
          remember: rememberDevice 
        }),
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        navigate("/dashboard/finance");
      } else {
        setError(data.message || "Code incorrect");
        setTwoFactorCode("");
      }
    } catch (err) {
      console.error("Erreur vérification 2FA:", err);
      setError(`Erreur de vérification: ${err.message}`);
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setVerifying(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/resend_2fa.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ user_id: userId }),
      });

      const data = await response.json();

      if (data.success) {
        setError("");
        alert("Un nouveau code vous a été envoyé");
      } else {
        setError(data.message || "Erreur lors de l'envoi du code");
      }
    } catch (err) {
      console.error("Erreur renvoi code:", err);
      setError(`Erreur de connexion: ${err.message}`);
    } finally {
      setVerifying(false);
    }
  };

  const handleBackToLogin = () => {
    setStep('login');
    setTwoFactorCode("");
    setError("");
  };

  const renderContent = () => {
    if (step === 'verify') {
      return (
        <div className="two-factor-step">
          <div className="text-center mb-4">
            <div className="twofa-icon-wrapper">
              <i className="ri-shield-keyhole-line"></i>
            </div>
            <h4 className="fw-bold mb-2" style={{ color: '#1e293b' }}>Authentification à deux facteurs</h4>
            <p className="text-muted small mb-0">
              Saisissez le code à 6 chiffres envoyé à votre application d'authentification
            </p>
          </div>

          <Form onSubmit={handleVerify2FA}>
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold small">Code de vérification</Form.Label>
              <Form.Control
                type="text"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                required
                size="lg"
                className="code-input"
                maxLength="6"
                disabled={verifying}
              />
              <Form.Text className="text-muted small">
                Entrez les 6 chiffres affichés dans votre application
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Check
                type="checkbox"
                id="remember-device"
                label="Mémoriser cet appareil pendant 30 jours"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                disabled={verifying}
                className="custom-checkbox"
              />
            </Form.Group>

            <Button 
              type="submit" 
              className="btn-sign w-100 mb-3"
              disabled={verifying || twoFactorCode.length !== 6}
              size="lg"
            >
              {verifying ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Vérification...
                </>
              ) : (
                "Vérifier"
              )}
            </Button>

            <div className="d-flex justify-content-between align-items-center">
              <Button 
                variant="link" 
                className="text-decoration-none p-0 small"
                onClick={handleResendCode}
                disabled={verifying}
                style={{ color: '#21ACCD' }}
              >
                <i className="ri-mail-send-line me-1"></i>
                Renvoyer le code
              </Button>
              
              <Button 
                variant="link" 
                className="text-decoration-none p-0 small text-muted"
                onClick={handleBackToLogin}
                disabled={verifying}
              >
                <i className="ri-arrow-left-line me-1"></i>
                Retour
              </Button>
            </div>
          </Form>
        </div>
      );
    }

    return (
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-4">
          <Form.Label className="fw-semibold small">Adresse Email</Form.Label>
          <div className="input-icon-wrapper">
            <i className="ri-mail-line input-icon"></i>
            <Form.Control 
              type="email" 
              placeholder="exemple@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
              size="lg"
              className="input-with-icon"
            />
          </div>
        </Form.Group>
        
        <Form.Group className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Form.Label className="fw-semibold small mb-0">Mot de passe</Form.Label>
            <Link to="/forgot-password" className="small text-decoration-none" style={{ color: '#21ACCD' }}>
              Mot de passe oublié?
            </Link>
          </div>
          <div className="password-input-wrapper">
            <i className="ri-lock-line password-icon"></i>
            <Form.Control 
              type={showPassword ? "text" : "password"}
              placeholder="Entrez votre mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
              className="password-input"
              size="lg"
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword(!showPassword)}
            >
              <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
            </button>
          </div>
        </Form.Group>
        
        <Button 
          type="submit" 
          className="btn-sign w-100 mt-3"
          disabled={loading}
          size="lg"
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2 text-white" />
              Connexion en cours...
            </>
          ) : (
            "Se connecter"
          )}
        </Button>
      </Form>
    );
  };

  return (
    <>
      <div className="page-sign d-block py-0">
        <Row className="g-0">
          <Col md="7" lg="5" xl="4" className="col-wrapper">
            <Card className="card-sign border-0 shadow-lg">
              <Card.Header className="bg-white border-0 pt-5 pb-0" align="center">
                <div className="logo-wrapper mb-3">
                  <img 
                    src={logo} 
                    className="logo-image" 
                    alt="Logo" 
                  />
                </div>
                <Card.Title className="h4 mb-2 fw-bold" style={{ color: '#1e293b' }}>
                  {step === 'verify' ? 'Vérification 2FA' : 'Authentification'}
                </Card.Title>
                <Card.Text className="text-muted small">
                  {step === 'verify' 
                    ? 'Sécurisez votre compte avec le code à 6 chiffres'
                    : 'Bienvenue! Veuillez vous connecter pour continuer.'
                  }
                </Card.Text>
              </Card.Header>
              <Card.Body className="px-4 pb-5">
                {error && (
                  <Alert variant="danger" onClose={() => setError("")} dismissible className="alert-modern mb-4">
                    <i className="ri-error-warning-line me-2"></i>
                    {error}
                  </Alert>
                )}
                
                {renderContent()}
              </Card.Body>
            </Card>
          </Col>
          
          <Col className="d-none d-lg-block position-relative">
            <div className="auth-overlay"></div>
            <img 
              src={bg2} 
              className="auth-img vh-100 object-fit-cover w-100" 
              alt="Authentification" 
              style={{ objectFit: 'cover' }}
            />
            <div className="auth-quote">
              <i className="ri-double-quotes-L"></i>
              <p>La gestion simplifiée de votre entreprise commence ici</p>
              <small>ERP Solution</small>
            </div>
          </Col>
        </Row>
      </div>

      <style>{`
        .page-sign {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        }

        .col-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          padding: 2rem;
          background: white;
        }

        .card-sign {
          width: 100%;
          max-width: 440px;
          border-radius: 32px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        /* Logo */
        .logo-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .logo-image {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          border: 3px solid #21ACCD;
          object-fit: cover;
          box-shadow: 0 8px 20px rgba(33, 172, 205, 0.2);
          transition: all 0.3s ease;
        }

        .logo-image:hover {
          transform: scale(1.05);
          box-shadow: 0 12px 28px rgba(33, 172, 205, 0.3);
        }

        /* Input avec icône */
        .input-icon-wrapper {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 1.1rem;
          z-index: 2;
        }

        .input-with-icon {
          padding-left: 2.75rem !important;
          border-radius: 14px;
          border: 1.5px solid #e2e8f0;
          transition: all 0.3s ease;
        }

        .input-with-icon:focus {
          border-color: #21ACCD;
          box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
        }

        /* Password input */
        .password-input-wrapper {
          position: relative;
        }

        .password-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 1.1rem;
          z-index: 2;
        }

        .password-input {
          padding-left: 2.75rem !important;
          padding-right: 3rem !important;
          border-radius: 14px;
          border: 1.5px solid #e2e8f0;
          transition: all 0.3s ease;
        }

        .password-input:focus {
          border-color: #21ACCD;
          box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
        }

        .password-toggle-btn {
          position: absolute;
          right: 0;
          top: 0;
          height: 100%;
          width: 45px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          z-index: 5;
          transition: color 0.3s ease;
          border-radius: 0 14px 14px 0;
        }

        .password-toggle-btn:hover {
          color: #21ACCD;
        }

        /* Bouton de connexion */
        .btn-sign {
          background: linear-gradient(135deg, #21ACCD 0%, #1a8ba8 100%);
          border: none;
          padding: 12px;
          font-weight: 600;
          border-radius: 14px;
          transition: all 0.3s ease;
          color: white;
        }

        .btn-sign:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px #8AC983;
          background:  #43A63A !important
        }

        .btn-sign:disabled {
          opacity: 0.7;
          transform: none;
        }

        /* 2FA Styles */
        .twofa-icon-wrapper {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #21ACCD20, #1a8ba820);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 15px;
        }

        .twofa-icon-wrapper i {
          font-size: 40px;
          color: #21ACCD;
        }

        .code-input {
          letter-spacing: 6px;
          font-size: 1.8rem;
          font-weight: 700;
          text-align: center;
          padding: 12px;
          border-radius: 14px;
          border: 1.5px solid #e2e8f0;
          font-family: monospace;
        }

        .code-input:focus {
          border-color: #21ACCD;
          box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
        }

        /* Custom checkbox */
        .custom-checkbox .form-check-input {
          width: 1.1rem;
          height: 1.1rem;
          margin-top: 0.15rem;
          border: 1.5px solid #cbd5e1;
          border-radius: 4px;
          cursor: pointer;
        }

        .custom-checkbox .form-check-input:checked {
          background-color: #21ACCD;
          border-color: #21ACCD;
        }

        .custom-checkbox .form-check-label {
          color: #64748b;
          font-size: 0.85rem;
          cursor: pointer;
        }

        /* Alert modern */
        .alert-modern {
          border: none;
          border-radius: 14px;
          padding: 1rem;
          background: #fef2f2;
          color: #dc2626;
        }

        /* Animation 2FA */
        .two-factor-step {
          animation: fadeInUp 0.5s ease-out;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Side image overlay et quote */
        .auth-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(33, 172, 205, 0.5), rgba(26, 139, 168, 0.6));
          z-index: 1;
        }

        .auth-img {
          position: relative;
          z-index: 0;
        }

        .auth-quote {
          position: absolute;
          bottom: 3rem;
          left: 2rem;
          right: 2rem;
          z-index: 2;
          color: white;
          text-align: center;
        }

        .auth-quote i {
          font-size: 2rem;
          opacity: 0.7;
          display: block;
          margin-bottom: 1rem;
        }

        .auth-quote p {
          font-size: 1.2rem;
          font-weight: 500;
          margin-bottom: 0.5rem;
          font-style: italic;
        }

        .auth-quote small {
          opacity: 0.8;
          font-size: 0.85rem;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .col-wrapper {
            padding: 1.5rem;
          }
          
          .card-sign {
            max-width: 100%;
          }
          
          .code-input {
            letter-spacing: 4px;
            font-size: 1.4rem;
          }
          
          .auth-quote {
            display: none;
          }
        }
      `}</style>
    </>
  );
}